'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

// ─────────────────────────────────────────────────────────────────────────────
// YAQEEN-WEIGHTED ALHAMDULILAH COUNTER
// ─────────────────────────────────────────────────────────────────────────────

export type YaqeenLevel = 'ilm' | 'ayn' | 'haqq';

export interface YaqeenRecognition {
  id: string;
  level: YaqeenLevel;
  description: string;
  timestamp: number;
  weight: number;
  moduleName: string;
}

const YAQEEN_WEIGHTS: Record<YaqeenLevel, number> = {
  ilm: 1,
  ayn: 3,
  haqq: 7,
};

const YAQEEN_META = {
  ilm: {
    label: 'Ilm Yaqeen',
    arabic: 'عِلْم يَقِين',
    color: '#2d7a47',
    bg: 'rgba(45,122,71,0.1)',
    border: 'rgba(45,122,71,0.3)',
    desc: 'Knowledge certainty — you were told',
    example: 'You read about the Names. You studied the Keys. You know the framework.',
    tension: null,
  },
  ayn: {
    label: 'Ayn Yaqeen',
    arabic: 'عَيْن يَقِين',
    color: '#2d8a87',
    bg: 'rgba(45,138,135,0.1)',
    border: 'rgba(45,138,135,0.3)',
    desc: 'Perceptual certainty — you were shown evidence',
    example: 'You saw the Names at work through the IRL Lens. You witnessed the data.',
    tension: 'Is seeing believing? The platform shows you evidence — but is that the same as experiencing it?',
  },
  haqq: {
    label: 'Haqq Yaqeen',
    arabic: 'حَقّ يَقِين',
    color: '#c9a84c',
    bg: 'rgba(201,168,76,0.1)',
    border: 'rgba(201,168,76,0.3)',
    desc: 'Reality certainty — you came and experienced it',
    example: 'You walked your ward. You held the leaf. You served in person. You closed the screen.',
    tension: null,
  },
};

export function YaqeenWeightedCounter() {
  const [recognitions, setRecognitions] = useState<YaqeenRecognition[]>([]);
  const [addingLevel, setAddingLevel] = useState<YaqeenLevel | null>(null);
  const [newDesc, setNewDesc] = useState('');
  const [newModule, setNewModule] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showTension, setShowTension] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('haal_yaqeen_recognitions');
    if (saved) {
      try { setRecognitions(JSON.parse(saved)); } catch {}
    }
  }, []);

  const saveRecognitions = (recs: YaqeenRecognition[]) => {
    setRecognitions(recs);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_yaqeen_recognitions', JSON.stringify(recs));
    }
  };

  const addRecognition = (level: YaqeenLevel) => {
    if (!newDesc.trim()) return;
    const rec: YaqeenRecognition = {
      id: `${Date.now()}-${Math.random()}`,
      level,
      description: newDesc.trim(),
      timestamp: Date.now(),
      weight: YAQEEN_WEIGHTS[level],
      moduleName: newModule.trim() || 'General',
    };
    saveRecognitions([rec, ...recognitions]);
    setNewDesc('');
    setNewModule('');
    setAddingLevel(null);
    if (level === 'ayn') setShowTension(true);
  };

  const totalWeight = recognitions.reduce((sum, r) => sum + r.weight, 0);
  const countByLevel = {
    ilm: recognitions.filter(r => r.level === 'ilm').length,
    ayn: recognitions.filter(r => r.level === 'ayn').length,
    haqq: recognitions.filter(r => r.level === 'haqq').length,
  };
  const weightByLevel = {
    ilm: recognitions.filter(r => r.level === 'ilm').reduce((s, r) => s + r.weight, 0),
    ayn: recognitions.filter(r => r.level === 'ayn').reduce((s, r) => s + r.weight, 0),
    haqq: recognitions.filter(r => r.level === 'haqq').reduce((s, r) => s + r.weight, 0),
  };

  return (
    <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 4 }}>
            ✦ Alhamdulilah Counter — Yaqeen Weighted
          </div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl' }}>
            الْحَمْدُ لِلَّه
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 28, color: '#c9a84c', fontWeight: 700 }}>{totalWeight}</div>
          <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em' }}>weighted total</div>
        </div>
      </div>

      {/* Level bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {(['ilm', 'ayn', 'haqq'] as YaqeenLevel[]).map(level => {
          const meta = YAQEEN_META[level];
          const pct = totalWeight > 0 ? (weightByLevel[level] / totalWeight) * 100 : 0;
          return (
            <div key={level}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: meta.color, letterSpacing: '.08em' }}>{meta.label}</span>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${meta.color}88`, direction: 'rtl' }}>{meta.arabic}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.5)' }}>
                    {countByLevel[level]}× (×{YAQEEN_WEIGHTS[level]}) = {weightByLevel[level]}
                  </span>
                  <button
                    onClick={() => setAddingLevel(addingLevel === level ? null : level)}
                    style={{
                      background: meta.bg,
                      border: `1px solid ${meta.border}`,
                      borderRadius: 3,
                      padding: '2px 8px',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                      fontSize: 7,
                      color: meta.color,
                      letterSpacing: '.08em',
                    }}
                  >
                    + Add
                  </button>
                </div>
              </div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.05)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: meta.color, borderRadius: 2, transition: 'width 0.5s ease' }} />
              </div>
              {addingLevel === level && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 4 }}>
                  <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic', marginBottom: 8, lineHeight: 1.5 }}>
                    {meta.example}
                  </div>
                  <input
                    value={newModule}
                    onChange={e => setNewModule(e.target.value)}
                    placeholder="Module / context (optional)"
                    style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 3, padding: '6px 10px', color: '#d4c5a9', fontSize: 11, marginBottom: 6, boxSizing: 'border-box' }}
                  />
                  <textarea
                    value={newDesc}
                    onChange={e => setNewDesc(e.target.value)}
                    placeholder={`Describe this ${meta.label} recognition...`}
                    rows={2}
                    style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 3, padding: '6px 10px', color: '#d4c5a9', fontSize: 11, resize: 'vertical', marginBottom: 8, boxSizing: 'border-box' }}
                  />
                  <button
                    onClick={() => addRecognition(level)}
                    style={{ background: meta.color, border: 'none', borderRadius: 3, padding: '6px 16px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 8, color: '#060d12', letterSpacing: '.1em', textTransform: 'uppercase' }}
                  >
                    Record — Alhamdulilah
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* "Is seeing believing?" tension note */}
      {showTension && (
        <div style={{
          background: 'rgba(45,138,135,0.06)',
          border: '1px solid rgba(45,138,135,0.2)',
          borderRadius: 4,
          padding: '10px 12px',
          marginBottom: 12,
        }}>
          <div style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.12em', textTransform: 'uppercase', color: '#2d8a87', marginBottom: 4 }}>
            ◎ Ayn Yaqeen Tension
          </div>
          <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7, fontStyle: 'italic' }}>
            "Having been presented evidence facilitated by media tools, is seeing believing?"
          </div>
          <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.45)', lineHeight: 1.6, marginTop: 6 }}>
            Everything you experience on this platform is at best Ayn Yaqeen. Haqq Yaqeen happens only when you close the platform and go outside.
          </div>
          <button onClick={() => setShowTension(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,138,135,0.5)', marginTop: 6, letterSpacing: '.08em' }}>
            Understood ✕
          </button>
        </div>
      )}

      {/* History toggle */}
      <button
        onClick={() => setShowHistory(!showHistory)}
        style={{ background: 'none', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3, padding: '5px 12px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', width: '100%' }}
      >
        {showHistory ? '▲ Hide' : '▼ Show'} Recognition History ({recognitions.length})
      </button>
      {showHistory && (
        <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {recognitions.length === 0 && (
            <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.35)', textAlign: 'center', padding: 12, fontStyle: 'italic' }}>
              No recognitions recorded yet. Begin with what is nearest.
            </div>
          )}
          {recognitions.map(rec => {
            const meta = YAQEEN_META[rec.level];
            return (
              <div key={rec.id} style={{ background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 3, padding: '6px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: meta.color, letterSpacing: '.06em' }}>{meta.label}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>×{rec.weight}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', marginLeft: 'auto' }}>{rec.moduleName}</span>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.7)', lineHeight: 1.5 }}>{rec.description}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QURAN ENCOUNTER MODULE
// ─────────────────────────────────────────────────────────────────────────────

interface QuranEncounterVerse {
  id: string;
  arabic: string;
  translation: string;
  reference: string;
  names: string[];
  sunnat: string;
  stateMatch: string[];
  domain: 'creation' | 'provision' | 'guidance' | 'return' | 'measure' | 'mercy';
}

const ENCOUNTER_VERSES: QuranEncounterVerse[] = [
  {
    id: 'v-41-53',
    arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ',
    translation: 'We will show them Our Signs in the horizons and in themselves until it becomes clear to them that it is the truth.',
    reference: 'Quran 41:53',
    names: ['Al-Haqq', 'Al-Basir', 'Al-Shahid'],
    sunnat: 'Signs appear in the external world and the internal self simultaneously — the same reality perceived from two angles.',
    stateMatch: ['observation', 'recognition', 'seeking'],
    domain: 'creation',
  },
  {
    id: 'v-2-164',
    arabic: 'إِنَّ فِي خَلْقِ السَّمَاوَاتِ وَالْأَرْضِ وَاخْتِلَافِ اللَّيْلِ وَالنَّهَارِ',
    translation: 'Indeed, in the creation of the heavens and the earth and the alternation of the night and the day are Signs for those of understanding.',
    reference: 'Quran 2:164',
    names: ['Al-Khaliq', 'Al-Alim', 'Al-Qadir'],
    sunnat: 'The entire cosmos is a text — every domain of creation is a verse in the same book.',
    stateMatch: ['study', 'environment', 'ecology'],
    domain: 'creation',
  },
  {
    id: 'v-87-1',
    arabic: 'سَبِّحِ اسْمَ رَبِّكَ الْأَعْلَى الَّذِي خَلَقَ فَسَوَّىٰ وَالَّذِي قَدَّرَ فَهَدَىٰ',
    translation: 'Glorify the name of your Lord, the Most High — Who created and proportioned, and Who measured then guided.',
    reference: 'Quran 87:1-3',
    names: ['Al-Ala', 'Al-Khaliq', 'Al-Hadi'],
    sunnat: 'Creation follows a sequence: origination → proportioning → measuring → guiding. Every created thing was measured before it was made.',
    stateMatch: ['beginning', 'orientation', 'purpose'],
    domain: 'guidance',
  },
  {
    id: 'v-15-21',
    arabic: 'وَإِن مِّن شَيْءٍ إِلَّا عِندَنَا خَزَائِنُهُ وَمَا نُنَزِّلُهُ إِلَّا بِقَدَرٍ مَّعْلُومٍ',
    translation: 'And there is not a thing but that its treasures are with Us, and We do not send it down except in a known measure.',
    reference: 'Quran 15:21',
    names: ['Ar-Razzaq', 'Al-Qadir', 'Al-Alim'],
    sunnat: 'Every provision has a stored treasure and a measured release. Nothing arrives by accident — everything comes in its appointed measure.',
    stateMatch: ['provision', 'scarcity', 'gratitude', 'ward'],
    domain: 'provision',
  },
  {
    id: 'v-54-49',
    arabic: 'إِنَّا كُلَّ شَيْءٍ خَلَقْنَاهُ بِقَدَرٍ',
    translation: 'Indeed, We created everything by measure. And Our command is but one, like the twinkling of an eye.',
    reference: 'Quran 54:49-50',
    names: ['Al-Qadir', 'Al-Muqtadir', 'Al-Khaliq'],
    sunnat: 'Taqdir — divine measure — is not fate imposed from outside but the precise proportion built into the nature of every created thing.',
    stateMatch: ['measure', 'design', 'proportion', 'ecology'],
    domain: 'measure',
  },
  {
    id: 'v-27-60',
    arabic: 'وَمَا كَانَ لَكُمْ أَن تُنبِتُوا شَجَرَهَا',
    translation: 'And We caused to grow beautiful gardens — not a tree of theirs was it for you to cause to grow.',
    reference: 'Quran 27:60',
    names: ['Al-Muhyi', 'Ar-Razzaq', 'Al-Musawwir'],
    sunnat: 'The beauty is real. The gardens are real. But you did not cause the tree to grow. The caretaker acts — the growing is Allah\'s.',
    stateMatch: ['garden', 'cultivation', 'effort', 'humility'],
    domain: 'provision',
  },
  {
    id: 'v-3-190',
    arabic: 'إِنَّ فِي خَلْقِ السَّمَاوَاتِ وَالْأَرْضِ وَاخْتِلَافِ اللَّيْلِ وَالنَّهَارِ لَآيَاتٍ لِّأُولِي الْأَلْبَابِ',
    translation: 'Indeed, in the creation of the heavens and the earth and the alternation of the night and the day are Signs for those of understanding.',
    reference: 'Quran 3:190',
    names: ['Al-Khaliq', 'Al-Qadir', 'Al-Alim'],
    sunnat: 'The Ulul Albab — those of understanding — are those who see the Signs in creation and connect them to their Source.',
    stateMatch: ['reflection', 'understanding', 'intellect'],
    domain: 'creation',
  },
  {
    id: 'v-22-66',
    arabic: 'وَهُوَ الَّذِي أَحْيَاكُمْ ثُمَّ يُمِيتُكُمْ ثُمَّ يُحْيِيكُمْ',
    translation: 'And He is the One Who gave you life, then will cause you to die, then will give you life again. Indeed, the human being is ungrateful.',
    reference: 'Quran 22:66',
    names: ['Al-Muhyi', 'Al-Mumit', 'Al-Hayy'],
    sunnat: 'The cycle of life-death-life is the Sunnat Allah of renewal. Every ending is a preparation for a new beginning.',
    stateMatch: ['loss', 'transition', 'renewal', 'gratitude'],
    domain: 'return',
  },
];

function surfaceVerseForState(recentModules: string[], recentNames: string[]): QuranEncounterVerse {
  // Simple state-based surfacing logic
  const stateKeywords: string[] = [];
  if (recentModules.includes('inner-reality-lens')) stateKeywords.push('observation', 'recognition');
  if (recentModules.includes('muslim-resource-guide')) stateKeywords.push('provision', 'ward');
  if (recentModules.includes('keys-to-kulli-haal')) stateKeywords.push('guidance', 'study');
  if (recentNames.includes('Ar-Razzaq')) stateKeywords.push('provision');
  if (recentNames.includes('Al-Khaliq')) stateKeywords.push('creation', 'ecology');

  const scored = ENCOUNTER_VERSES.map(v => ({
    verse: v,
    score: v.stateMatch.filter(s => stateKeywords.includes(s)).length,
  }));
  scored.sort((a, b) => b.score - a.score);

  // Return highest scored, or random if tie
  const topScore = scored[0].score;
  const topVerses = scored.filter(s => s.score === topScore);
  return topVerses[Math.floor(Math.random() * topVerses.length)].verse;
}

export function QuranEncounterModule() {
  const [currentVerse, setCurrentVerse] = useState<QuranEncounterVerse | null>(null);
  const [activeTab, setActiveTab] = useState<'encounter' | 'three-questions' | 'names' | 'sunnat' | 'amanah'>('encounter');
  const [isItAnswer, setIsItAnswer] = useState('');
  const [whatIsItAnswer, setWhatIsItAnswer] = useState('');
  const [whenIsItAnswer, setWhenIsItAnswer] = useState('');
  const [haqqDeclaration, setHaqqDeclaration] = useState('');
  const [amanahBridge, setAmanahBridge] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const recentModules = JSON.parse(localStorage.getItem('haal_recent_modules') || '[]');
    const recentNames = JSON.parse(localStorage.getItem('haal_recent_names') || '[]');
    const verse = surfaceVerseForState(recentModules, recentNames);
    setCurrentVerse(verse);
  }, []);

  const refreshVerse = () => {
    const idx = Math.floor(Math.random() * ENCOUNTER_VERSES.length);
    setCurrentVerse(ENCOUNTER_VERSES[idx]);
    setActiveTab('encounter');
    setIsItAnswer('');
    setWhatIsItAnswer('');
    setWhenIsItAnswer('');
    setHaqqDeclaration('');
    setAmanahBridge('');
    setSaved(false);
  };

  const saveEncounter = () => {
    if (!currentVerse) return;
    const encounters = JSON.parse(localStorage.getItem('haal_quran_encounters') || '[]');
    encounters.unshift({
      verseId: currentVerse.id,
      reference: currentVerse.reference,
      isIt: isItAnswer,
      whatIsIt: whatIsItAnswer,
      whenIsIt: whenIsItAnswer,
      haqq: haqqDeclaration,
      amanah: amanahBridge,
      timestamp: Date.now(),
    });
    localStorage.setItem('haal_quran_encounters', JSON.stringify(encounters.slice(0, 50)));
    setSaved(true);
  };

  if (!currentVerse) return null;

  const TABS = [
    { id: 'encounter', label: 'Encounter', icon: '◎' },
    { id: 'three-questions', label: 'Three Questions', icon: '✦' },
    { id: 'names', label: 'Names', icon: '⬡' },
    { id: 'sunnat', label: 'Sunnat', icon: '◈' },
    { id: 'amanah', label: 'Amanah', icon: '⌘' },
  ] as const;

  return (
    <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid rgba(138,138,184,0.1)', background: 'rgba(138,138,184,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.6)', marginBottom: 3 }}>
              ✦ Quran Encounter Module — Revelation Track
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.6)', letterSpacing: '.04em' }}>
              Parallel to the IRL Lens — Ayat in Revelation
            </div>
          </div>
          <button
            onClick={refreshVerse}
            style={{ background: 'rgba(138,138,184,0.08)', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 3, padding: '5px 10px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 7, color: 'rgba(138,138,184,0.6)', letterSpacing: '.1em', textTransform: 'uppercase' }}
          >
            ↻ New Verse
          </button>
        </div>
        {/* Verse display */}
        <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(138,138,184,0.8)', direction: 'rtl', lineHeight: 1.8, marginBottom: 8 }}>
            {currentVerse.arabic}
          </div>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.65)', fontStyle: 'italic', lineHeight: 1.7, maxWidth: 500, margin: '0 auto 6px' }}>
            "{currentVerse.translation}"
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(138,138,184,0.5)', letterSpacing: '.1em' }}>
            {currentVerse.reference}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(138,138,184,0.1)', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: '0 0 auto',
              padding: '8px 14px',
              background: activeTab === tab.id ? 'rgba(138,138,184,0.1)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #8a8ab8' : '2px solid transparent',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: 8,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: activeTab === tab.id ? '#8a8ab8' : 'rgba(212,197,169,0.4)',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 20 }}>
        {activeTab === 'encounter' && (
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(138,138,184,0.6)', letterSpacing: '.1em', marginBottom: 12 }}>
              This verse was surfaced based on your current state and recent activity. It arrived — not because you chose it.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {currentVerse.names.map(name => (
                <span key={name} style={{ fontFamily: 'monospace', fontSize: 8, padding: '3px 10px', background: 'rgba(138,138,184,0.08)', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 20, color: '#8a8ab8', letterSpacing: '.06em' }}>
                  {name}
                </span>
              ))}
            </div>
            <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 4 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,138,135,0.6)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                ◎ Parallel Track — IRL Lens
              </div>
              <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.6 }}>
                The IRL Lens reads Ayat in Creation. This module reads Ayat in Revelation. Both tracks run parallel — the same Names appearing in both. When Ar-Razzaq appears in a verse and Ar-Razzaq was extracted from a leaf observation — the bridge forms.
              </div>
            </div>
          </div>
        )}

        {activeTab === 'three-questions' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', marginBottom: 4 }}>
              Apply the Three Questions to this verse — the same methodology as the IRL Lens, turned toward Revelation.
            </div>
            {[
              { q: 'Is it?', arabic: 'هَلْ هُوَ؟', desc: 'Does this verse address your current state? Does it exist for you right now?', val: isItAnswer, set: setIsItAnswer },
              { q: 'What is it?', arabic: 'مَا هُوَ؟', desc: 'What Names and Attributes does this verse contain? What commands, what reminders?', val: whatIsItAnswer, set: setWhatIsItAnswer },
              { q: 'When is it?', arabic: 'مَتَى هُوَ؟', desc: 'What in your journey brought this verse to you now? What Huduth Zamani made this verse timely?', val: whenIsItAnswer, set: setWhenIsItAnswer },
            ].map(({ q, arabic, desc, val, set }) => (
              <div key={q}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#8a8ab8', fontWeight: 600 }}>{q}</span>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: 'rgba(138,138,184,0.5)', direction: 'rtl' }}>{arabic}</span>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', fontStyle: 'italic', marginBottom: 6, lineHeight: 1.5 }}>{desc}</div>
                <textarea
                  value={val}
                  onChange={e => set(e.target.value)}
                  rows={2}
                  placeholder={`Your response to "${q}"...`}
                  style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(138,138,184,0.15)', borderRadius: 3, padding: '8px 10px', color: '#d4c5a9', fontSize: 11, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'names' && (
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', marginBottom: 14 }}>
              Names extracted from this verse — cross-link to Names you have encountered in creation.
            </div>
            {currentVerse.names.map(name => (
              <div key={name} style={{ background: 'rgba(138,138,184,0.06)', border: '1px solid rgba(138,138,184,0.15)', borderRadius: 4, padding: '10px 14px', marginBottom: 8 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#8a8ab8', marginBottom: 4 }}>{name}</div>
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.6 }}>
                  This Name appears in Revelation. Has it appeared in your creation observations? When the same Name appears in both tracks — the bridge forms.
                </div>
                <Link href="/names-reference" style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(138,138,184,0.5)', letterSpacing: '.1em', textDecoration: 'none', display: 'inline-block', marginTop: 6 }}>
                  → View in Names Reference
                </Link>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'sunnat' && (
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 10 }}>
              Sunnat Allah — The Pattern
            </div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.8)', lineHeight: 1.8, fontStyle: 'italic', marginBottom: 14, padding: '12px 16px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 4 }}>
              "{currentVerse.sunnat}"
            </div>
            <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7 }}>
              What Sunnat Allah does this verse describe? How does this pattern in Revelation mirror a pattern you have observed in creation?
            </div>
            <textarea
              value={haqqDeclaration}
              onChange={e => setHaqqDeclaration(e.target.value)}
              rows={3}
              placeholder="This verse is telling me that..."
              style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 3, padding: '8px 10px', color: '#d4c5a9', fontSize: 11, resize: 'vertical', marginTop: 12, boxSizing: 'border-box' }}
            />
          </div>
        )}

        {activeTab === 'amanah' && (
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)', marginBottom: 10 }}>
              Amanah — The Trust This Verse Calls You To
            </div>
            <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, marginBottom: 12 }}>
              What action does this verse demand? Bridge it to the MRG or to a personal practice.
            </div>
            <textarea
              value={amanahBridge}
              onChange={e => setAmanahBridge(e.target.value)}
              rows={3}
              placeholder="This verse calls me to..."
              style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 3, padding: '8px 10px', color: '#d4c5a9', fontSize: 11, resize: 'vertical', marginBottom: 12, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={saveEncounter}
                disabled={saved}
                style={{ background: saved ? 'rgba(45,122,71,0.2)' : 'rgba(45,122,71,0.15)', border: `1px solid ${saved ? 'rgba(45,122,71,0.4)' : 'rgba(45,122,71,0.3)'}`, borderRadius: 3, padding: '8px 16px', cursor: saved ? 'default' : 'pointer', fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase' }}
              >
                {saved ? '✓ Encounter Saved' : 'Save Encounter'}
              </button>
              <Link href="/muslim-resource-guide" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 3, padding: '8px 14px', fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.08em', textDecoration: 'none' }}>
                ⬡ Bridge to MRG
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INNER HAWAS MODULE
// ─────────────────────────────────────────────────────────────────────────────

const INNER_NAMES_MAP: Record<string, { names: string[]; description: string }> = {
  protected: { names: ['Al-Hafiz', 'Al-Wali'], description: 'The feeling of protection reveals Al-Hafiz — The Preserver — operating through your inner state.' },
  constricted: { names: ['Al-Qabid'], description: 'Constriction reveals Al-Qabid — The Withholder — the divine contraction that precedes expansion.' },
  expanded: { names: ['Al-Basit'], description: 'Expansion reveals Al-Basit — The Expander — the opening of the heart.' },
  grateful: { names: ['Al-Hamid', 'Al-Shakur'], description: 'Gratitude reveals Al-Hamid — The Praiseworthy — and Al-Shakur — The Appreciative.' },
  anxious: { names: ['Al-Hafiz', 'Al-Wakil'], description: 'Anxiety is a call toward Al-Wakil — The Trustee — the One in Whose hands all affairs rest.' },
  peaceful: { names: ['As-Salam', 'Al-Mumin'], description: 'Peace reveals As-Salam — The Source of Peace — present in the inner state.' },
  purposeful: { names: ['Al-Hadi', 'Al-Rashid'], description: 'Purposefulness reveals Al-Hadi — The Guide — directing from within.' },
  lost: { names: ['Al-Hadi', 'Al-Wali'], description: 'Feeling lost is a call toward Al-Hadi — The Guide — Who guides from within and without.' },
  connected: { names: ['Al-Wadud', 'Al-Jami'], description: 'Connection reveals Al-Wadud — The Loving — and Al-Jami — The Gatherer.' },
  isolated: { names: ['Al-Qarib', 'Al-Mujib'], description: 'Isolation is a call toward Al-Qarib — The Near — Who is closer than your jugular vein.' },
};

export function InnerHawasModule() {
  const [innerState, setInnerState] = useState('');
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [isItAnswer, setIsItAnswer] = useState('');
  const [whatIsItAnswer, setWhatIsItAnswer] = useState('');
  const [whenIsItAnswer, setWhenIsItAnswer] = useState('');
  const [activeTab, setActiveTab] = useState<'encounter' | 'fission' | 'names'>('encounter');

  const stateOptions = Object.keys(INNER_NAMES_MAP);
  const matchedNames = selectedState ? INNER_NAMES_MAP[selectedState] : null;

  return (
    <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(90,90,138,0.2)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid rgba(90,90,138,0.1)', background: 'rgba(90,90,138,0.04)' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(90,90,138,0.6)', marginBottom: 4 }}>
          ◎ Inner Hawas — Ayat Within You
        </div>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#d4c5a9', fontWeight: 400, marginBottom: 4 }}>
          The IRL Lens Turned Inward
        </div>
        <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.45)', lineHeight: 1.6 }}>
          Some Ayat are inherent within us. The inner world is a field of signs — apply the Three Questions to your inner state.
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid rgba(90,90,138,0.1)' }}>
        {[
          { id: 'encounter', label: 'Encounter' },
          { id: 'fission', label: 'Fission' },
          { id: 'names', label: 'Names' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: activeTab === tab.id ? 'rgba(90,90,138,0.1)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #5a5a8a' : '2px solid transparent',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: 8,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: activeTab === tab.id ? '#8a8ab8' : 'rgba(212,197,169,0.35)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 20 }}>
        {activeTab === 'encounter' && (
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em', marginBottom: 14, lineHeight: 1.6 }}>
              Wujud — Does this state exist? Acknowledge it before examining it.
            </div>
            <textarea
              value={innerState}
              onChange={e => setInnerState(e.target.value)}
              rows={3}
              placeholder="What is your inner state right now? Describe it without judgment..."
              style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(90,90,138,0.2)', borderRadius: 3, padding: '8px 10px', color: '#d4c5a9', fontSize: 11, resize: 'vertical', marginBottom: 14, boxSizing: 'border-box' }}
            />
            <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', marginBottom: 8 }}>
              Or select a state to begin fission:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {stateOptions.map(state => (
                <button
                  key={state}
                  onClick={() => setSelectedState(selectedState === state ? null : state)}
                  style={{
                    background: selectedState === state ? 'rgba(90,90,138,0.2)' : 'rgba(90,90,138,0.06)',
                    border: `1px solid ${selectedState === state ? 'rgba(90,90,138,0.4)' : 'rgba(90,90,138,0.15)'}`,
                    borderRadius: 20,
                    padding: '4px 12px',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: 8,
                    color: selectedState === state ? '#8a8ab8' : 'rgba(212,197,169,0.5)',
                    letterSpacing: '.06em',
                    transition: 'all 0.15s',
                  }}
                >
                  {state}
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'fission' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', marginBottom: 4 }}>
              Apply the Three Questions to your inner state — the fission process turned inward.
            </div>
            {[
              { q: 'Is this state?', arabic: 'هَلْ هَذِهِ الْحَال؟', desc: 'Wujud — does this emotion, this thought, this impulse exist? Acknowledge it.', val: isItAnswer, set: setIsItAnswer },
              { q: 'What is this state?', arabic: 'مَا هَذِهِ الْحَال؟', desc: 'Huduth Dhati — what is the essential nature of this feeling? What is it, really?', val: whatIsItAnswer, set: setWhatIsItAnswer },
              { q: 'When is this state?', arabic: 'مَتَى هَذِهِ الْحَال؟', desc: 'Huduth Zamani — what brought this state about? What will it lead to?', val: whenIsItAnswer, set: setWhenIsItAnswer },
            ].map(({ q, arabic, desc, val, set }) => (
              <div key={q}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#8a8ab8', fontWeight: 600 }}>{q}</span>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: 'rgba(138,138,184,0.5)', direction: 'rtl' }}>{arabic}</span>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.45)', fontStyle: 'italic', marginBottom: 6, lineHeight: 1.5 }}>{desc}</div>
                <textarea
                  value={val}
                  onChange={e => set(e.target.value)}
                  rows={2}
                  placeholder={`${q}...`}
                  style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(90,90,138,0.15)', borderRadius: 3, padding: '7px 10px', color: '#d4c5a9', fontSize: 11, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'names' && (
          <div>
            <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.45)', lineHeight: 1.7, marginBottom: 14 }}>
              Which Names of Allah are manifest in your inner state? The inner world is Ayat — signs pointing back to their Source.
            </div>
            {selectedState && matchedNames ? (
              <div style={{ background: 'rgba(90,90,138,0.08)', border: '1px solid rgba(90,90,138,0.2)', borderRadius: 4, padding: '12px 16px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#8a8ab8', letterSpacing: '.08em', marginBottom: 6 }}>
                  State: {selectedState}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {matchedNames.names.map(name => (
                    <span key={name} style={{ fontFamily: 'monospace', fontSize: 9, padding: '3px 10px', background: 'rgba(138,138,184,0.1)', border: '1px solid rgba(138,138,184,0.25)', borderRadius: 20, color: '#8a8ab8' }}>
                      {name}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.7 }}>
                  {matchedNames.description}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.3)', fontStyle: 'italic', textAlign: 'center', padding: 20 }}>
                Select a state in the Encounter tab to see which Names are manifest within it.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRIPARTITE AYAT SYSTEM
// ─────────────────────────────────────────────────────────────────────────────

export function TripartiteAyatSystem() {
  const [activeTrack, setActiveTrack] = useState<'within' | 'around' | 'revealed' | null>(null);
  const [trackActivity, setTrackActivity] = useState({ within: 0, around: 0, revealed: 0 });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const encounters = JSON.parse(localStorage.getItem('haal_quran_encounters') || '[]');
    const observations = JSON.parse(localStorage.getItem('haal_observations') || '[]');
    const innerHawas = JSON.parse(localStorage.getItem('haal_inner_hawas') || '[]');
    setTrackActivity({
      within: innerHawas.length + 1,
      around: observations.length + 1,
      revealed: encounters.length + 1,
    });
  }, []);

  const allActive = trackActivity.within > 0 && trackActivity.around > 0 && trackActivity.revealed > 0;
  const luminosity = Math.min(1, (trackActivity.within + trackActivity.around + trackActivity.revealed) / 15);

  const tracks = [
    {
      id: 'within' as const,
      label: 'Ayat Within Us',
      arabic: 'آيَات فِي أَنفُسِنَا',
      icon: '◎',
      color: '#5a5a8a',
      desc: 'Signs inherent in the self — emotions, states, impulses as Ayat',
      module: 'Inner Hawas',
      verse: '"And in yourselves — do you not see?" — 51:21',
      href: '/user-dashboard?tab=garden',
    },
    {
      id: 'around' as const,
      label: 'Ayat Around Us',
      arabic: 'آيَات فِي الْآفَاق',
      icon: '⬡',
      color: '#2d8a87',
      desc: 'Signs in creation — ecology, ward, community as Ayat',
      module: 'IRL Lens',
      verse: '"We will show them Our Signs in the horizons" — 41:53',
      href: '/inner-reality-lens',
    },
    {
      id: 'revealed' as const,
      label: 'Ayat Revealed to Us',
      arabic: 'آيَات مُنَزَّلَة',
      icon: '✦',
      color: '#8a8ab8',
      desc: 'Signs in Revelation — Quran, Sunnah as Ayat',
      module: 'Quran Encounter',
      verse: '"And in themselves" — the showing itself is Revelation — 41:53',
      href: '/user-dashboard?tab=garden',
    },
  ];

  return (
    <div style={{
      background: allActive
        ? `radial-gradient(ellipse at 50% 50%, rgba(201,168,76,${luminosity * 0.08}) 0%, rgba(6,13,18,0.9) 70%)`
        : 'rgba(6,13,18,0.8)',
      border: `1px solid ${allActive ? 'rgba(201,168,76,0.25)' : 'rgba(201,168,76,0.1)'}`,
      borderRadius: 8,
      padding: 20,
      transition: 'all 0.5s ease',
    }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 4 }}>
          ✦ Three Ayat Tracks — One Reality
        </div>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#d4c5a9', marginBottom: 6 }}>
          Within Us / Around Us / Revealed to Us
        </div>
        {allActive && (
          <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.7)', letterSpacing: '.08em', padding: '4px 10px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 3, display: 'inline-block' }}>
            ✦ All three tracks active — garden at full luminosity
          </div>
        )}
      </div>

      {/* Luminosity indicator */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Garden Luminosity</span>
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.6)' }}>{Math.round(luminosity * 100)}%</span>
        </div>
        <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${luminosity * 100}%`, background: 'linear-gradient(to right, #2d7a47, #2d8a87, #c9a84c)', borderRadius: 2, transition: 'width 0.5s ease' }} />
        </div>
        <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.35)', marginTop: 4, fontStyle: 'italic' }}>
          Luminosity increases when all three tracks are active simultaneously.
        </div>
      </div>

      {/* Three tracks */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 10 }}>
        {tracks.map(track => {
          const activity = trackActivity[track.id];
          const isActive = activity > 0;
          return (
            <div
              key={track.id}
              onClick={() => setActiveTrack(activeTrack === track.id ? null : track.id)}
              style={{
                background: isActive ? `${track.color}12` : 'rgba(6,13,18,0.4)',
                border: `1px solid ${isActive ? `${track.color}33` : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 6,
                padding: '12px 14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 14, color: isActive ? track.color : 'rgba(212,197,169,0.2)' }}>{track.icon}</span>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? track.color : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }} />
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 8, color: isActive ? track.color : 'rgba(212,197,169,0.35)', letterSpacing: '.06em', marginBottom: 3 }}>
                {track.label}
              </div>
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${track.color}66`, direction: 'rtl', marginBottom: 6 }}>
                {track.arabic}
              </div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.45)', lineHeight: 1.5, marginBottom: 8 }}>
                {track.desc}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 7, color: `${track.color}88`, letterSpacing: '.06em', marginBottom: 6 }}>
                Module: {track.module}
              </div>
              {activeTrack === track.id && (
                <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(6,13,18,0.5)', borderRadius: 3, border: `1px solid ${track.color}22` }}>
                  <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic', lineHeight: 1.6, marginBottom: 8 }}>
                    {track.verse}
                  </div>
                  <Link href={track.href} style={{ fontFamily: 'monospace', fontSize: 7, color: track.color, letterSpacing: '.1em', textDecoration: 'none', textTransform: 'uppercase' }}>
                    → Open {track.module}
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 41:53 anchor */}
      <div style={{ marginTop: 14, textAlign: 'center', padding: '10px 0', borderTop: '1px solid rgba(201,168,76,0.08)' }}>
        <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(201,168,76,0.4)', direction: 'rtl', marginBottom: 3 }}>
          سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em' }}>
          "In the horizons" (around) + "in themselves" (within) — the showing is Revelation. Three tracks, one verse. — 41:53
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPERFECTION MODEL
// ─────────────────────────────────────────────────────────────────────────────

export function ImperfectionModel() {
  const [dormancyDays, setDormancyDays] = useState(0);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [capacityMode, setCapacityMode] = useState<'full' | 'partial' | 'minimal'>('full');
  const [showCapacityToggle, setShowCapacityToggle] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const lastVisit = localStorage.getItem('haal_last_visit_date');
    if (lastVisit) {
      const days = Math.floor((Date.now() - new Date(lastVisit).getTime()) / (1000 * 60 * 60 * 24));
      setDormancyDays(days);
      if (days >= 3) setShowWelcomeBack(true);
    }
    const savedCapacity = localStorage.getItem('haal_capacity_mode') as any;
    if (savedCapacity) setCapacityMode(savedCapacity);
  }, []);

  const setCapacity = (mode: 'full' | 'partial' | 'minimal') => {
    setCapacityMode(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_capacity_mode', mode);
    }
  };

  const CAPACITY_META = {
    full: { label: 'Full Capacity', color: '#2d7a47', desc: 'Engaged and available for deep observation' },
    partial: { label: 'Partial Capacity', color: '#D4A82A', desc: 'Managing responsibilities — lighter engagement' },
    minimal: { label: 'Minimal Capacity', color: '#8a8ab8', desc: 'Constrained circumstances — one genuine observation carries full weight' },
  };

  return (
    <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 8, padding: 20 }}>
      <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', marginBottom: 12 }}>
        ⬡ The Garden Models Imperfection
      </div>

      {/* Welcome back message */}
      {showWelcomeBack && (
        <div style={{
          background: 'rgba(45,122,71,0.08)',
          border: '1px solid rgba(45,122,71,0.2)',
          borderRadius: 6,
          padding: '14px 16px',
          marginBottom: 14,
        }}>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#d4c5a9', marginBottom: 6 }}>
            Your soil rested. It's richer now.
          </div>
          <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7 }}>
            {dormancyDays} days of dormancy. The garden does not punish rest — fallow periods regenerate the soil.
            "Perfection is neither expected nor what we're made for."
          </div>
          <button
            onClick={() => setShowWelcomeBack(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', marginTop: 8, letterSpacing: '.08em' }}
          >
            Continue ✕
          </button>
        </div>
      )}

      {/* Capacity toggle */}
      <div style={{ marginBottom: 14 }}>
        <button
          onClick={() => setShowCapacityToggle(!showCapacityToggle)}
          style={{
            width: '100%',
            background: `${CAPACITY_META[capacityMode].color}10`,
            border: `1px solid ${CAPACITY_META[capacityMode].color}30`,
            borderRadius: 4,
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: 'left',
          }}
        >
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: CAPACITY_META[capacityMode].color, marginBottom: 2 }}>
              Current Capacity
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.7)' }}>
              {CAPACITY_META[capacityMode].label}
            </div>
          </div>
          <span style={{ fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>{showCapacityToggle ? '▲' : '▼'}</span>
        </button>

        {showCapacityToggle && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.entries(CAPACITY_META).map(([key, meta]) => (
              <button
                key={key}
                onClick={() => { setCapacity(key as any); setShowCapacityToggle(false); }}
                style={{
                  background: capacityMode === key ? `${meta.color}12` : 'rgba(6,13,18,0.4)',
                  border: `1px solid ${capacityMode === key ? `${meta.color}30` : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: 3,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontFamily: 'monospace', fontSize: 8, color: meta.color, marginBottom: 2 }}>{meta.label}</div>
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.4 }}>{meta.desc}</div>
              </button>
            ))}
            <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.35)', fontStyle: 'italic', padding: '6px 4px', lineHeight: 1.5 }}>
              "To the best of our ability" — the metric is relative to capacity. One genuine observation under constraint carries the weight of ten made with abundance.
            </div>
          </div>
        )}
      </div>

      {/* Imperfection principles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { icon: '◎', label: 'Seasonal Dormancy', desc: 'Periods of inactivity are rest, not failure. The soil needs fallow periods to regenerate.' },
          { icon: '◈', label: 'Observation Revision', desc: 'Observations can be revised — the original remains as a layer beneath. Growth includes the slipping.' },
          { icon: '⬡', label: 'Compost Function', desc: 'Old observations decompose into soil nutrients. Nothing is wasted — everything transforms.' },
          { icon: '✦', label: 'Capacity-Relative Metrics', desc: 'The Mizan recognizes that the same seed grows differently in different conditions.' },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', gap: 10, padding: '8px 10px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.1)', borderRadius: 4 }}>
            <span style={{ fontSize: 12, color: 'rgba(45,122,71,0.6)', flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.06em', marginBottom: 3 }}>{item.label}</div>
              <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 4 }}>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic', lineHeight: 1.7 }}>
          "To slip and grow and struggle, to put in effort and strive in the Deen, Fisabilillah, to the best of our ability. Perfection is neither expected nor what we're made for."
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.4)', marginTop: 4, letterSpacing: '.08em' }}>
          — Open Letter to Imprisoned Caretakers
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMMUNITY ROLE MODULE CREATION
// ─────────────────────────────────────────────────────────────────────────────

interface RoleModule {
  id: string;
  vocation: string;
  lensName: string;
  createdBy: string;
  irlEntryPoint: string;
  keysPath: string;
  mrgDomain: string;
  prompt: string;
  timestamp: number;
}

const SEED_ROLES: RoleModule[] = [
  {
    id: 'engineer-lens',
    vocation: 'Engineer',
    lensName: 'Engineer Lens',
    createdBy: 'Roots Ma\'rifah',
    irlEntryPoint: 'What tools is this engineer working on, and what tools does this engineer utilize toward that purpose?',
    keysPath: 'Key 03 (Taqdir — Measure) → Key 05 (Amanah — Trust)',
    mrgDomain: 'Infrastructure & Environmental Systems',
    prompt: 'How would an engineer approach the custodianship of the systems they build?',
    timestamp: 0,
  },
  {
    id: 'teacher-lens',
    vocation: 'Teacher',
    lensName: 'Teacher Lens',
    createdBy: 'Roots Ma\'rifah',
    irlEntryPoint: 'What knowledge is being transmitted, and what does the student already carry?',
    keysPath: 'Key 01 (Ayat — Signs) → Key 04 (Hikmah — Wisdom)',
    mrgDomain: 'Education & Youth Development',
    prompt: 'How would a teacher recognize the Fitra already present in each student?',
    timestamp: 0,
  },
  {
    id: 'farmer-lens',
    vocation: 'Farmer',
    lensName: 'Cultivator Lens',
    createdBy: 'Roots Ma\'rifah',
    irlEntryPoint: 'What does the soil need? What season is this?',
    keysPath: 'Key 02 (Sunnat Allah — Divine Pattern) → Key 05 (Amanah)',
    mrgDomain: 'Food Security & Urban Agriculture',
    prompt: 'How would a farmer approach the custodianship of endangered habitats?',
    timestamp: 0,
  },
  {
    id: 'native-lens',
    vocation: 'Native / Indigenous',
    lensName: 'Land Keeper Lens',
    createdBy: 'Roots Ma\'rifah',
    irlEntryPoint: 'What does this land remember that we have forgotten?',
    keysPath: 'Key 02 (Sunnat Allah) → Key 06 (Khalifah — Stewardship)',
    mrgDomain: 'Environmental Justice & Land Rights',
    prompt: 'How would a land keeper read the signs that the land itself is giving?',
    timestamp: 0,
  },
  {
    id: 'returning-citizen-lens',
    vocation: 'Returning Citizen',
    lensName: 'Restorer Lens',
    createdBy: 'Roots Ma\'rifah',
    irlEntryPoint: 'What has been broken, and what does restoration look like from the inside?',
    keysPath: 'Key 05 (Amanah) → Key 07 (Islah — Restoration)',
    mrgDomain: 'Reentry Support & Justice',
    prompt: 'How would a returning citizen approach the Islah of systems that failed them?',
    timestamp: 0,
  },
  {
    id: 'scientist-lens',
    vocation: 'Scientist',
    lensName: 'Observer Lens',
    createdBy: 'Roots Ma\'rifah',
    irlEntryPoint: 'What does the data reveal that the hypothesis did not predict?',
    keysPath: 'Key 01 (Ayat) → Key 03 (Taqdir — Measure)',
    mrgDomain: 'Research & Environmental Data',
    prompt: 'How would a scientist apply the Three Questions to their own methodology?',
    timestamp: 0,
  },
];

export function CommunityRoleModuleCreator() {
  const [communityRoles, setCommunityRoles] = useState<RoleModule[]>([]);
  const [showCreator, setShowCreator] = useState(false);
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [newRole, setNewRole] = useState({ vocation: '', lensName: '', irlEntryPoint: '', keysPath: '', mrgDomain: '', prompt: '' });
  const [creatorName, setCreatorName] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('haal_community_roles');
    if (saved) {
      try { setCommunityRoles(JSON.parse(saved)); } catch {}
    }
  }, []);

  const saveRole = () => {
    if (!newRole.vocation.trim() || !newRole.prompt.trim()) return;
    const role: RoleModule = {
      id: `role-${Date.now()}`,
      ...newRole,
      createdBy: creatorName.trim() || 'Community Caretaker',
      timestamp: Date.now(),
    };
    const updated = [role, ...communityRoles];
    setCommunityRoles(updated);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_community_roles', JSON.stringify(updated));
    }
    setNewRole({ vocation: '', lensName: '', irlEntryPoint: '', keysPath: '', mrgDomain: '', prompt: '' });
    setCreatorName('');
    setShowCreator(false);
  };

  const allRoles = [...SEED_ROLES, ...communityRoles];

  return (
    <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 4 }}>
            ⌘ Community Role Modules
          </div>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#d4c5a9' }}>
            Roots Ma'rifah — Six-Role Grid
          </div>
          <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', marginTop: 4, lineHeight: 1.5 }}>
            "What if we modeled all the possible roles of our current society?" — The community creates the role lenses. The platform provides the method.
          </div>
        </div>
        <button
          onClick={() => setShowCreator(!showCreator)}
          style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 3, padding: '6px 12px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 7, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', flexShrink: 0 }}
        >
          + Create Lens
        </button>
      </div>

      {/* Creator form */}
      {showCreator && (
        <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, padding: 16, marginBottom: 14 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.6)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>
            Create a New Role Lens
          </div>
          {[
            { key: 'vocation', label: 'Vocation / Role', placeholder: 'e.g. Nurse, Janitor, Warehouse Worker...' },
            { key: 'lensName', label: 'Lens Name', placeholder: 'e.g. Healer Lens, Custodian Lens...' },
            { key: 'irlEntryPoint', label: 'IRL Entry Point', placeholder: 'What question does this role ask of creation?' },
            { key: 'keysPath', label: 'Keys Path', placeholder: 'Which Keys does this role engage first?' },
            { key: 'mrgDomain', label: 'MRG Domain', placeholder: 'Which MRG category does this role serve?' },
            { key: 'prompt', label: 'Role Prompt', placeholder: 'How would this role approach the custodianship of...' },
          ].map(field => (
            <div key={field.key} style={{ marginBottom: 8 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.08em', marginBottom: 3 }}>{field.label}</div>
              <input
                value={(newRole as any)[field.key]}
                onChange={e => setNewRole(prev => ({ ...prev, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 3, padding: '6px 10px', color: '#d4c5a9', fontSize: 11, boxSizing: 'border-box' }}
              />
            </div>
          ))}
          <input
            value={creatorName}
            onChange={e => setCreatorName(e.target.value)}
            placeholder="Your name / handle (optional)"
            style={{ width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3, padding: '6px 10px', color: '#d4c5a9', fontSize: 11, marginBottom: 10, boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={saveRole} style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 3, padding: '7px 16px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase' }}>
              Plant This Lens
            </button>
            <button onClick={() => setShowCreator(false)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, padding: '7px 12px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Role grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 8 }}>
        {allRoles.map(role => (
          <div
            key={role.id}
            onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
            style={{
              background: expandedRole === role.id ? 'rgba(201,168,76,0.06)' : 'rgba(6,13,18,0.5)',
              border: `1px solid ${expandedRole === role.id ? 'rgba(201,168,76,0.2)' : 'rgba(201,168,76,0.08)'}`,
              borderRadius: 5,
              padding: '10px 12px',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#c9a84c', letterSpacing: '.06em' }}>{role.lensName || role.vocation}</div>
              <span style={{ fontSize: 7, color: 'rgba(201,168,76,0.3)' }}>{expandedRole === role.id ? '▲' : '▼'}</span>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em', marginBottom: expandedRole === role.id ? 10 : 0 }}>
              {role.vocation} — by {role.createdBy}
            </div>
            {expandedRole === role.id && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {[
                  { label: 'IRL Entry', value: role.irlEntryPoint },
                  { label: 'Keys Path', value: role.keysPath },
                  { label: 'MRG Domain', value: role.mrgDomain },
                  { label: 'Role Prompt', value: role.prompt },
                ].map(item => item.value ? (
                  <div key={item.label} style={{ padding: '6px 8px', background: 'rgba(6,13,18,0.4)', borderRadius: 3, borderLeft: '2px solid rgba(201,168,76,0.2)' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.08em', marginBottom: 3 }}>{item.label}</div>
                    <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.65)', lineHeight: 1.5 }}>{item.value}</div>
                  </div>
                ) : null)}
              </div>
            )}
          </div>
        ))}
      </div>

      {communityRoles.length > 0 && (
        <div style={{ marginTop: 10, fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.35)', letterSpacing: '.08em', textAlign: 'center' }}>
          {communityRoles.length} community-created lens{communityRoles.length !== 1 ? 'es' : ''} planted
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "IS SEEING BELIEVING?" TENSION NOTE
// ─────────────────────────────────────────────────────────────────────────────

export function AynYaqeenTensionNote({ context = 'platform' }: { context?: string }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div style={{
      background: 'rgba(45,138,135,0.06)',
      border: '1px solid rgba(45,138,135,0.2)',
      borderRadius: 4,
      padding: '10px 14px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    }}>
      <span style={{ fontSize: 12, color: '#2d8a87', flexShrink: 0, marginTop: 1 }}>◎</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: '#2d8a87', marginBottom: 4 }}>
          Ayn Yaqeen — {context}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.65)', lineHeight: 1.7, fontStyle: 'italic' }}>
          "Having been presented evidence facilitated by media tools, is seeing believing?"
        </div>
        <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6, marginTop: 4 }}>
          What you experience here is at best Ayn Yaqeen — perceptual certainty through evidence. Haqq Yaqeen — reality certainty — happens only when you close the platform and go outside.
        </div>
      </div>
      <button onClick={() => setDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(45,138,135,0.4)', fontSize: 12, flexShrink: 0 }}>✕</button>
    </div>
  );
}
